import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';

/**
 * The "Links" panel every detail page carries, as one component.
 *
 * Two kinds of link go in here and both are worth having:
 *
 *   - the **entity** this record points at — its user, its purchase;
 *   - a **prefilled search** — "everyone on this purchase", which is just
 *     `/next/participants?purchaseId=…`.
 *
 * The second kind is nearly free and is what makes the admin navigable: every
 * list in this tree reads its filters from the URL, so a link *is* a search. It
 * is also the difference between "copy this id, open that page, paste it" and
 * one click, which is most of what using this admin consists of.
 *
 * `to` routes inside the tree; `href` leaves it for the old admin and is marked,
 * so a full page load is never a surprise. Falsy entries are dropped, which lets
 * a page write `user.id && { … }` inline instead of building the array in two
 * steps.
 */
export default function RelatedLinks({ items, empty = 'No link.' }) {
  const links = (items || []).filter(Boolean);

  return (
    <div className="next-links">
      {links.length === 0 && <span className="next-muted">{empty}</span>}

      {links.map(({ label, hint, to, href }) => {
        const body = (
          <>
            <span className="next-links__label">{label}</span>
            {hint && <span className="next-links__hint">{hint}</span>}
            {href && <span className="next-links__out" aria-label="Opens the old admin">↗</span>}
          </>
        );

        return to
          ? <Link className="next-links__item" key={label} to={to}>{body}</Link>
          : <a className="next-links__item" key={label} href={href}>{body}</a>;
      })}
    </div>
  );
}

RelatedLinks.propTypes = {
  items: PropTypes.arrayOf(PropTypes.oneOfType([
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      hint: PropTypes.string,
      to: PropTypes.string,
      href: PropTypes.string
    }),
    // Pages drop entries with `condition && {…}`, so `false` is expected.
    PropTypes.oneOf([false, null, undefined])
  ])).isRequired,
  empty: PropTypes.string
};
